import { Container, DisplayObject, FederatedPointerEvent, Graphics } from 'pixi.js';
import type { FOSProjectionPoint, FOSSignalProjectionPoint } from '../../../utils/api';
import { drawDiamond } from './fosCanvasDrawing';

export interface FOSSignalPointCoords {
  x: number;
  y: number;
}

export interface FOSSignalPointScales {
  x: (value: number) => number;
  y: (value: number) => number;
}

export interface FOSSignalHoverPayload {
  signalId: string;
  x: number;
  y: number;
}

export interface RenderFOSSignalPointsOptions {
  container: Container;
  signalGraphicsById: Map<string, Graphics>;
  signalProjections?: FOSSignalProjectionPoint[] | null;
  showSignals: boolean;
  scales: FOSSignalPointScales;
  decisionCoordMap: Map<string, FOSSignalPointCoords>;
  onSignalClick?: (signalId: string) => void;
  onSignalHoverStart: (hover: FOSSignalHoverPayload) => void;
  onSignalHoverMove: (hover: FOSSignalHoverPayload) => void;
  onSignalHoverEnd: () => void;
  onInvalidSignal?: (signal: FOSSignalProjectionPoint) => void;
}

type LegacySignalProjectionPoint = FOSSignalProjectionPoint & {
  nearest_decisions?: string[] | null;
};

const SIGNAL_COLOR = 0xf59e0b;
const SIGNAL_HIGHLIGHT_COLOR = 0xfbbf24;
const SIGNAL_SIZE = 9;
const SIGNAL_HIGHLIGHT_SIZE = 11;

export function buildFOSDecisionCoordMap(
  points?: FOSProjectionPoint[] | null
): Map<string, FOSSignalPointCoords> {
  if (!points) {
    return new Map();
  }

  return new Map(
    points.map((point) => [
      point.decision_id,
      { x: point.x, y: point.y },
    ])
  );
}

export function resolveFOSSignalCoords(
  signal: FOSSignalProjectionPoint,
  decisionCoordMap: Map<string, FOSSignalPointCoords>
): FOSSignalPointCoords | null {
  const rawX = Number(signal.x);
  const rawY = Number(signal.y);

  if (Number.isFinite(rawX) && Number.isFinite(rawY)) {
    return { x: rawX, y: rawY };
  }

  const legacySignal = signal as LegacySignalProjectionPoint;
  const fallbackIds = signal.nearest_decision_ids ?? legacySignal.nearest_decisions ?? [];
  for (const decisionId of fallbackIds) {
    const coords = decisionCoordMap.get(decisionId);
    if (coords) {
      return coords;
    }
  }

  return null;
}

export function renderFOSSignalPoints({
  container,
  signalGraphicsById,
  signalProjections,
  showSignals,
  scales,
  decisionCoordMap,
  onSignalClick,
  onSignalHoverStart,
  onSignalHoverMove,
  onSignalHoverEnd,
  onInvalidSignal,
}: RenderFOSSignalPointsOptions): void {
  container.removeChildren();
  signalGraphicsById.clear();

  if (!showSignals || !signalProjections || signalProjections.length === 0) {
    return;
  }

  signalProjections.forEach((signal) => {
    const coords = resolveFOSSignalCoords(signal, decisionCoordMap);
    if (!coords) {
      onInvalidSignal?.(signal);
      return;
    }

    const x = scales.x(coords.x);
    const y = scales.y(coords.y);
    const graphics = new Graphics();

    drawDiamond(graphics, x, y, SIGNAL_SIZE, SIGNAL_COLOR, 0.9);
    graphics.interactive = true;
    graphics.cursor = 'pointer';

    graphics.on('pointerover', (event: FederatedPointerEvent) => {
      graphics.clear();
      graphics.lineStyle(2, 0x111827);
      drawDiamond(graphics, x, y, SIGNAL_HIGHLIGHT_SIZE, SIGNAL_HIGHLIGHT_COLOR, 1);

      onSignalHoverStart({
        signalId: signal.signal_id,
        x: event.global.x,
        y: event.global.y,
      });
    });

    graphics.on('pointermove', (event: FederatedPointerEvent) => {
      onSignalHoverMove({
        signalId: signal.signal_id,
        x: event.global.x,
        y: event.global.y,
      });
    });

    graphics.on('pointerout', () => {
      graphics.clear();
      drawDiamond(graphics, x, y, SIGNAL_SIZE, SIGNAL_COLOR, 0.9);
      onSignalHoverEnd();
    });

    graphics.on('pointertap', () => {
      onSignalClick?.(signal.signal_id);
    });

    container.addChild(graphics as DisplayObject);
    signalGraphicsById.set(signal.signal_id, graphics);
  });
}
